import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Facebook, Instagram, Menu, X } from "lucide-react";
import { scrollToHash } from "@/utils/scroll";
import { useLang } from "@/i18n";

const NAV = [
    { hash: "#inicio", id: "inicio" },
    { hash: "#nosotros", id: "nosotros" },
    { hash: "#habitaciones", id: "habitaciones" },
    { hash: "#resenas", id: "resenas" },
    { hash: "#contacto", id: "contacto" },
];

function LangToggle() {
    const { lang, setLang, t } = useLang();
    return (
        <div
            className="flex items-center rounded-full border border-cream/30 p-0.5"
            data-testid="lang-toggle"
            role="group"
            aria-label={t.nav.language}
        >
            {["es", "en"].map((l) => (
                <button
                    key={l}
                    type="button"
                    onClick={() => setLang(l)}
                    aria-pressed={lang === l}
                    data-testid={`lang-toggle-${l}`}
                    className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                        lang === l ? "bg-cream text-olive" : "text-cream/80 hover:text-cream"
                    }`}
                >
                    {l}
                </button>
            ))}
        </div>
    );
}

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { t } = useLang();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (hash) => {
        setOpen(false);
        if (pathname !== "/") {
            navigate("/");
            setTimeout(() => scrollToHash(hash), 350);
        } else {
            scrollToHash(hash);
        }
    };

    const solid = scrolled || pathname !== "/";

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    solid
                        ? "bg-olive shadow-[0_10px_30px_rgba(45,51,25,0.18)]"
                        : "bg-olive-deep/30 backdrop-blur-md"
                }`}
            >
                <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 md:px-12">
                    <button
                        type="button"
                        onClick={() => go("#inicio")}
                        data-testid="logo-button"
                        className="flex items-center"
                        aria-label={t.nav.home}
                    >
                        <img
                            src="/fotos/logo-horizontal.png"
                            alt="Hotel Punta Palmeras — Bungalows & Hotel"
                            width="341"
                            height="120"
                            className="h-10 w-auto brightness-0 invert md:h-11"
                        />
                    </button>

                    <nav aria-label="Navegación principal" className="hidden items-center gap-7 lg:flex">
                        {NAV.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => go(item.hash)}
                                data-testid={`nav-link-${item.id}`}
                                className="text-sm font-medium tracking-wide text-cream/85 transition-colors duration-300 hover:text-amber-soft"
                            >
                                {t.nav[item.id]}
                            </button>
                        ))}
                    </nav>

                    <div className="hidden items-center gap-4 lg:flex">
                        <a
                            href="https://www.facebook.com/HotelPuntaPalmeras"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Facebook del hotel"
                            data-testid="social-facebook"
                            className="text-cream/80 transition-colors duration-300 hover:text-amber-soft"
                        >
                            <Facebook className="h-4 w-4" strokeWidth={1.5} />
                        </a>
                        <a
                            href="https://www.instagram.com/hotelpuntapalmeras/"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram del hotel"
                            data-testid="social-instagram"
                            className="text-cream/80 transition-colors duration-300 hover:text-amber-soft"
                        >
                            <Instagram className="h-4 w-4" strokeWidth={1.5} />
                        </a>
                        <LangToggle />
                        <button
                            type="button"
                            onClick={() => go("#habitaciones")}
                            data-testid="header-reservar-button"
                            className="rounded-full bg-amber px-6 py-2.5 text-sm font-medium text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-amber-dark hover:shadow-lg"
                        >
                            {t.nav.reservar}
                        </button>
                    </div>

                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        aria-label="Abrir menú"
                        data-testid="mobile-menu-open-button"
                        className="text-cream lg:hidden"
                    >
                        <Menu className="h-7 w-7" strokeWidth={1.5} />
                    </button>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        data-testid="mobile-menu"
                        className="fixed inset-0 z-[60] flex flex-col bg-olive-deep/95 backdrop-blur-xl"
                    >
                        <div className="flex h-20 items-center justify-between px-6">
                            <img
                                src="/fotos/logo-horizontal.png"
                                alt="Hotel Punta Palmeras"
                                width="341"
                                height="120"
                                className="h-9 w-auto brightness-0 invert"
                            />
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                aria-label={t.nav.close}
                                data-testid="mobile-menu-close-button"
                                className="text-cream"
                            >
                                <X className="h-7 w-7" strokeWidth={1.5} />
                            </button>
                        </div>
                        <nav aria-label="Menú móvil" className="flex flex-1 flex-col items-start justify-center gap-2 px-8">
                            {NAV.map((item, i) => (
                                <motion.button
                                    key={item.id}
                                    type="button"
                                    initial={{ opacity: 0, y: 24 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    onClick={() => go(item.hash)}
                                    data-testid={`mobile-nav-link-${item.id}`}
                                    className="py-2 font-display text-4xl font-light text-cream transition-colors duration-300 hover:text-amber-soft"
                                >
                                    {t.nav[item.id]}
                                </motion.button>
                            ))}
                            <motion.button
                                type="button"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.45, duration: 0.5 }}
                                onClick={() => go("#habitaciones")}
                                data-testid="mobile-reservar-button"
                                className="mt-8 rounded-full bg-amber px-8 py-4 text-base font-medium text-white"
                            >
                                {t.nav.reservar}
                            </motion.button>
                            <div className="mt-6">
                                <LangToggle />
                            </div>
                        </nav>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
